import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { RefreshCw, TriangleAlert } from 'lucide-react'
import { api, ApiUnreachableError } from '@/api/client'
import type {
  OrphanGapRow,
  OrphanGapStage,
  OrphanGapSnapshotProperty,
  OrphanGapTruncation,
} from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { EmptyState } from '@/components/ui/empty-state'
import { formatCurrency } from '@/lib/format-utils'

/**
 * Orphan-gap offers: unbookable nights between two bookings, and who could be
 * offered them.
 *
 * Read-only by design, and deliberately has no Send control. The send path is
 * a later slice of docs/orphan-gap-offers-plan.md and does not exist in
 * admin-api yet, and this workspace's UX rules are explicit that a control
 * with no backend behind it must not be shown. Everything here answers "what
 * is there, at what price, and why can I or can I not act on it" — the
 * question the detection slice was built to answer.
 *
 * **The page reads a precomputed snapshot; it does not scan.** It used to fire
 * one request per property at two-way concurrency, each fetching up to a year
 * of PriceLabs calendar, so opening the page meant watching twenty properties
 * being evaluated for an answer that changes about once a day. A daily cron
 * and an explicit Rescan do that work now, one queue job per property, and
 * this renders what they stored — which is also the only shape available,
 * since admin-api evaluates exactly one property per call by design.
 *
 * Because the answer is stored rather than live, the page says WHEN it was
 * computed. "No orphan nights" this morning and the same words from last week
 * are different claims, and only one of them is worth acting on.
 */

/**
 * Human-readable reasons a property's answer is incomplete.
 *
 * Every one of these means "we could not establish this", never "there is
 * nothing here" — which is exactly the distinction an empty table cannot make
 * on its own. Without surfacing them, a quiet week and a PriceLabs outage look
 * identical to the operator.
 */
const TRUNCATION_LABELS: Record<keyof Omit<OrphanGapTruncation, 'propertyId'>, string> = {
  reservations: 'Booking history was too long to read in full, so this property was skipped.',
  reservationsStale:
    'The booking sync is stale, errored, or missing, so occupancy could not be trusted.',
  reservationCoverage:
    'Some dates fall outside the range the booking sync has actually fetched, so gaps there were withheld.',
  ownerBlocks: 'Owner blocks could not be read in full, so this property was skipped.',
  calendar: 'PriceLabs data was incomplete, so affected gaps were withheld until it is available.',
  sendableChecks:
    'Not every conversation could be checked this run — the furthest-out gaps are checked on a later run.',
}

function truncationReasons(t: OrphanGapTruncation): string[] {
  return (Object.keys(TRUNCATION_LABELS) as Array<keyof typeof TRUNCATION_LABELS>)
    .filter(key => t[key])
    .map(key => TRUNCATION_LABELS[key])
}

const STAGE_LABELS: Record<string, string> = {
  extend: 'Stay longer',
  arrive_earlier: 'Arrive earlier',
}

/** "2026-08-14" -> "Fri 14 Aug". Built explicitly; Intl inserts a comma here. */
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function formatNight(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return iso
  return `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`
}

function formatGapDates(row: OrphanGapRow): string {
  return row.firstNight === row.lastNight
    ? formatNight(row.firstNight)
    : `${formatNight(row.firstNight)} – ${formatNight(row.lastNight)}`
}

/**
 * The one-line scan status.
 *
 * Three rules it exists to keep. It avoids "0 of 0" and "0 offerable, 0
 * blocked", because this workspace's UX rules rule out empty counters and a
 * tally of nothing says less than a sentence does. It never claims nothing was
 * found while some property could not actually be checked — a conclusive
 * negative sitting next to a banner saying the answer is unknown is worse than
 * either message alone, because the confident one is the one an operator acts
 * on. And it always says WHEN, because what is on screen was computed by a
 * scheduled scan rather than by opening the page: an answer with no age is not
 * one an operator can weigh.
 */
function scanStatus({
  awaiting,
  running,
  pending,
  unanswered,
  answered,
  total,
  computedAt,
  actionable,
  blocked,
  unchecked,
  unknown,
}: {
  awaiting: boolean
  running: boolean
  pending: number
  unanswered: number
  answered: number
  total: number
  computedAt: string | null
  actionable: number
  blocked: number
  /** Answered this run, but incompletely - what "returned an incomplete answer" counts. */
  unchecked: number
  /** Every way the page is short of the whole picture, including unknowns this run has not reached. */
  unknown: number
}): string {
  // Between an accepted Rescan and the snapshot that confirms it, every count
  // on hand still describes the run BEFORE it - so "Rescanning — 0 still to
  // go" and "Scanning N of N" were both being narrated from stale numbers.
  // Say the one thing that is actually known yet.
  if (awaiting) return 'Scan started — waiting for the first results…'
  if (total === 0) return 'No properties to scan.'
  if (running && !computedAt) return `Scanning ${total - pending} of ${total}…`
  // Never scanned is not a finding. Saying "no orphan nights found" here would
  // report a scan that has not happened as one that came back empty - the same
  // conflation this page exists to avoid everywhere else.
  if (!computedAt) {
    return `${total} ${total === 1 ? 'property' : 'properties'} to scan. Nothing has been computed yet — press Rescan, or wait for the daily scan.`
  }
  const age = computedAt ? relativeAge(computedAt) : null
  // Counted from properties that have actually ANSWERED, not from the size of
  // the portfolio. A hard-coded total contradicted its own next sentence -
  // "Scanned 20 properties … 19 still to go" - and described a run that
  // stopped short as having scanned everything.
  //
  // The age is the OLDEST answer's, so "computed 2 hours ago" is a claim that
  // holds for every row on the page rather than for whichever property
  // happened to be scanned last.
  const scannedLabel = `${
    answered === total
      ? `Scanned ${total} ${total === 1 ? 'property' : 'properties'}`
      : `Scanned ${answered} of ${total} properties`
  }${age ? `, ${age}` : ''}`
  // Two different things a non-answering property can mean, and they call for
  // opposite reactions: wait, or look into it. A run past its window has
  // stopped — its jobs dead-lettered, or the consumer died — so saying "still
  // to go" there would promise an answer that is not coming.
  const stillGoing = running
    ? ` Scanning — ${pending} still to go.`
    : unanswered > 0
      ? ` The last scan ended without answering for ${unanswered} of them.`
      : ''
  const caveat =
    unchecked > 0
      ? ` ${unchecked} returned an incomplete answer, so this is not the whole picture.`
      : ''
  // The conclusive negative is gated on EVERY unknown, not just the ones this
  // run answered incompletely - a property it has not reached, or one carrying
  // a failure from the last scan that did, leaves the same hole. They stay
  // worded apart below, because "returned an incomplete answer", "was never
  // reached" and "we have not looked yet" are different things to go and do
  // something about.
  if (actionable === 0 && blocked === 0) {
    return unknown > 0
      ? `${scannedLabel}. Nothing found on the ones that answered.${caveat}${stillGoing}`
      : `${scannedLabel}. No orphan nights found.${stillGoing}`
  }
  const parts = []
  if (actionable > 0) parts.push(`${actionable} offerable`)
  if (blocked > 0) parts.push(`${blocked} blocked`)
  return `${scannedLabel}. ${parts.join(', ')}.${caveat}${stillGoing}`
}

/**
 * Whether a scan we started has yet to appear in a snapshot.
 *
 * Polling cannot key off the last snapshot's `running` alone: if the refetch
 * meant to confirm a newly started run exhausts its retries, that snapshot
 * still says `running: false`, so the interval never starts, the accepted scan
 * runs to completion unseen, and the button re-enables - letting a second
 * portfolio scan be queued over the first.
 *
 * Compared by START TIME rather than by matching run ids, and read straight
 * off the mutation rather than held in state. Both follow from the same
 * requirement: this has to stop being true on its own. A remembered id never
 * does - once the daily cron replaces `run_id`, a later refetch reads it as an
 * unconfirmed scan all over again and holds Rescan disabled over a run that
 * finished hours ago. Any run at least as new as ours settles the question,
 * whether it is ours or the one that superseded it.
 */
function notYetLanded(snapshotStartedAt?: string, startedAt?: string): boolean {
  if (!startedAt) return false
  return !snapshotStartedAt || snapshotStartedAt < startedAt
}

/** "2 hours ago", or null for a timestamp we cannot read. */
function relativeAge(iso: string): string | null {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return null
  return formatDistanceToNow(at, { addSuffix: true })
}

/**
 * Money, through the app's shared formatter rather than a local `$` template.
 *
 * No currency argument: `GET /admin/orphan-offers` does not carry one, so this
 * falls back to `formatCurrency`'s USD default - which is correct for every
 * property today (checked against the live pricing calendar) but is a default,
 * not a fact about the data. Genuinely supporting a non-USD property means
 * admin-api propagating the reservation's currency through `GapPricing`
 * first; using the shared formatter here is what makes that a one-argument
 * change instead of a rewrite.
 */
function money(amount: number): string {
  return formatCurrency(amount)
}

/**
 * The per-night ask, as the operator needs to sanity-check it.
 *
 * Shows the guest's own nightly rate beside the suggestion, because the whole
 * point of the operator review step is being able to see what a night normally
 * earns before deciding whether the discount is right — a bare "$150 a night"
 * is not reviewable.
 */
function StagePrice({ stage }: { stage: OrphanGapStage }) {
  if (!stage.pricing) return <span className="text-textMuted">—</span>
  const { pricing } = stage
  const perNight = pricing.uniform ? pricing.nights[0]?.suggestedNightly : null
  return (
    <div className="space-y-0.5">
      <div className="font-medium text-text">
        {perNight != null ? `${money(perNight)} a night` : `${money(pricing.offerValue)} total`}
      </div>
      <div className="text-xs text-textMuted">
        they paid {money(pricing.paidNightly)}/night · {money(pricing.offerValue)} total
      </div>
    </div>
  )
}

function StageCell({ stage }: { stage: OrphanGapStage }) {
  const label = STAGE_LABELS[stage.stage] ?? stage.stage
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={stage.eligible ? 'default' : 'secondary'}>{label}</Badge>
        {stage.guestName && <span className="text-sm text-text">{stage.guestName}</span>}
        {stage.channel && <span className="text-xs text-textMuted">{stage.channel}</span>}
      </div>
      {!stage.eligible && stage.reasonLabel && (
        <p className="text-xs text-textMuted">{stage.reasonLabel}</p>
      )}
    </div>
  )
}

/**
 * One table row PER STAGE, with the gap's own cells spanning them.
 *
 * Not two vertical lists in separate cells: `StageCell` and `StagePrice` have
 * different natural heights, and a blocked stage adds a reason line, so the
 * lists drift apart and a guest's price ends up beside the wrong stage. Same
 * row means the association cannot come apart, whatever either cell contains.
 */
function GapRows({ row, stale }: { row: OrphanGapRow; stale?: boolean }) {
  const tint = row.actionable ? undefined : 'opacity-70'
  return (
    <>
      {row.stages.map((stage, index) => (
        <TableRow key={`${row.gapKey}:${stage.stage}`} className={tint}>
          {index === 0 && (
            <>
              <TableCell className="align-top" rowSpan={row.stages.length}>
                <div className="font-medium text-text">{row.propertyName || row.propertyId}</div>
                <div className="text-xs text-textMuted">{formatGapDates(row)}</div>
                {/* A scan in progress has not reached this property yet, so
                    this gap is the last scan's answer. Shown rather than
                    hidden - it is still the best information there is about
                    that property - but not passed off as freshly checked. */}
                {stale && (
                  <div className="text-xs text-textMuted italic">
                    From the last scan, not rechecked yet
                  </div>
                )}
              </TableCell>
              <TableCell className="align-top whitespace-nowrap" rowSpan={row.stages.length}>
                {row.gapNights} {row.gapNights === 1 ? 'night' : 'nights'}
                <div className="text-xs text-textMuted">min stay {row.minStay}</div>
              </TableCell>
            </>
          )}
          <TableCell className="align-top">
            <StageCell stage={stage} />
          </TableCell>
          <TableCell className="align-top">
            <StagePrice stage={stage} />
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}

export function OrphanOffersPage() {
  const queryClient = useQueryClient()

  // Declared before the query because the query's own `refetchInterval` reads
  // its result: a scan we started that no snapshot has shown yet keeps polling.
  const rescan = useMutation({
    mutationFn: () => api.scanOrphanGaps(),
    // Refetch immediately so `running` flips true and the poll below starts;
    // without it the page sits still until the next manual reload even though
    // a scan is under way.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['orphan-gap-snapshot'] }),
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['orphan-gap-snapshot'],
    queryFn: () => api.getOrphanGapSnapshot(),
    // Poll while a scan is working through the queue, or while one we started
    // has not yet appeared in a snapshot. Otherwise stop: a standing interval
    // would re-read a snapshot that only changes once a day.
    refetchInterval: query => {
      const snapshot = query.state.data
      if (snapshot?.running) return 5000
      if (notYetLanded(snapshot?.run?.started_at, rescan.data?.run?.started_at)) return 5000
      return false
    },
    // Left ON deliberately, where the fan-out version turned it off. That
    // version's refetch was twenty requests and a year of PriceLabs calendar
    // per property, so a focus refetch was a real cost. This one is a few KV
    // reads, and an idle page has no interval - so without it, a tab left open
    // across the daily scan would show yesterday's rows forever, under a
    // relative age frozen at whatever it said when it last rendered.
    refetchOnWindowFocus: true,
  })

  // A relative age only recomputes when something renders, and an idle
  // snapshot has no refetch interval - so on a page left open and focused,
  // "about 1 hour ago" could sit there for hours while quietly becoming
  // false. `refetchOnWindowFocus` covers coming back to the tab; this covers
  // never leaving it.
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick(tick => tick + 1), 60_000)
    return () => clearInterval(id)
  }, [])

  if (isLoading) return <LoadingSkeleton />
  // The full-page error state only when there is nothing to show. Once a
  // snapshot has loaded, react-query keeps it while a later poll fails, and
  // replacing it with an error screen would throw away operational values the
  // operator was already reading over a transient backend blip - AGENTS.md is
  // explicit that previously shown values are preserved on failure. A failed
  // refresh is reported alongside the retained snapshot instead.
  if (error && !data) {
    return <ErrorState error={error instanceof Error ? error : new Error('Unknown error')} />
  }

  const properties: OrphanGapSnapshotProperty[] = data?.properties ?? []
  const total = properties.length
  // `running` comes from the server, which knows which properties the current
  // run has actually answered — the client cannot infer that from row counts,
  // since a property with no gaps and a property not yet scanned both
  // contribute nothing.
  // A scan we started and have not yet seen counts as running too, so the
  // button stays disabled rather than inviting a second one over the first.
  // A scan we started and have not yet seen: the button stays disabled rather
  // than inviting a second one over the first, and the status line says so
  // instead of reciting counts that still belong to the previous run.
  const awaiting =
    rescan.isPending || notYetLanded(data?.run?.started_at, rescan.data?.run?.started_at)
  const running = Boolean(data?.running) || awaiting
  // Taken per property rather than from the flat aggregate, so each row knows
  // whether the scan in progress has actually rechecked it.
  const rows = properties.flatMap(property =>
    property.rows.map(row => ({ row, stale: property.pending }))
  )

  const actionable = rows.filter(entry => entry.row.actionable)
  const blocked = rows.filter(entry => !entry.row.actionable)
  // A pending property's error or truncation belongs to the LAST scan that
  // reached it, not to the one in progress - it still carries that answer
  // while it waits. Attributing it to the current run produced "Scanned 0 of 1
  // properties" beside "1 returned an incomplete answer" about a property this
  // scan has not touched. Split rather than dropped: the property is still an
  // unknown right now, and hiding what is known about it mid-rescan is the
  // opposite of what this banner is for.
  const carriesTruncation = (property: OrphanGapSnapshotProperty) =>
    Boolean(property.truncation && truncationReasons(property.truncation).length)
  const incomplete = properties.filter(p => !p.pending && carriesTruncation(p))
  const failed = properties.filter(p => !p.pending && p.error)
  const stale = properties.filter(p => p.pending && (carriesTruncation(p) || p.error))
  // Every way this page can be looking at less than the whole picture. A
  // property the run never reached belongs here as much as one that answered
  // incompletely: in both cases an empty table is an unknown, not a finding.
  // Everything this run has not settled: not reached yet, still being retried,
  // or never scanned at all. A property mid-rescan whose previous answer was
  // clean and empty contributes no error and no truncation, so counting only
  // those let a conclusive "no orphan nights found" appear before the run had
  // checked anything - the one claim this page must never make.
  //
  // `stale` and admin-api's `unanswered` are both subsets of this, so they are
  // not added again; `unknown` is a yes/no gate, and the honest question it
  // asks is whether anything at all is unsettled.
  const unresolved = properties.filter(
    property => property.pending || property.retrying || !property.computedAt
  ).length
  const unknown = incomplete.length + failed.length + unresolved

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            {/* No title here: the topbar already names the page, and this
                workspace's UX rules say it appears once. */}
            <CardDescription>
              Nights stranded between two bookings, too short to sell on their own. Offer them to a
              guest on one side rather than losing them.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => rescan.mutate()} disabled={running}>
            <RefreshCw className={`mr-2 h-4 w-4 ${running ? 'animate-spin' : ''}`} />
            {running ? 'Scanning…' : 'Rescan'}
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {/* No empty counters: a scan that has found nothing yet says so in
              words, and the tallies appear only once there is something to
              tally. */}
          {/* A failed trigger has to say so and keep saying so. The button
              simply re-enabling while yesterday's snapshot stayed on screen
              would leave an operator believing the scan they asked for is
              under way — the failure mode a toast is worst at, since it is
              gone by the time they look back. The stale snapshot itself is
              preserved rather than cleared, per AGENTS.md. */}
          {rescan.isError && (
            <div className="space-y-2" role="alert">
              <p className="text-sm text-danger">
                Could not start a scan:{' '}
                {rescan.error instanceof Error ? rescan.error.message : 'something went wrong'}.
                What is shown below is the previous scan.
              </p>
              {/* A failed POST neither invalidates nor fails the snapshot
                  query, so the query branch's link never appears - and an
                  expired session's message tells the operator to click below
                  to re-authenticate. */}
              {rescan.error instanceof ApiUnreachableError && rescan.error.baseUrl && (
                <Button asChild size="sm">
                  <a href={rescan.error.baseUrl} target="_blank" rel="noopener noreferrer">
                    Re-authenticate
                  </a>
                </Button>
              )}
            </div>
          )}
          {error && (
            <div className="space-y-2" role="alert">
              <p className="text-sm text-danger">
                Could not refresh: {error instanceof Error ? error.message : 'something went wrong'}
                . What is shown below is the last snapshot that loaded.
              </p>
              {/* An expired Cloudflare Access session says "click below to
                  re-authenticate" in its own message, so printing that message
                  without the link leaves the operator told to click something
                  that is not there. `ErrorState` renders this action on the
                  full-page path; keeping the snapshot must not cost it. */}
              {error instanceof ApiUnreachableError && error.baseUrl && (
                <Button asChild size="sm">
                  <a href={error.baseUrl} target="_blank" rel="noopener noreferrer">
                    Re-authenticate
                  </a>
                </Button>
              )}
            </div>
          )}
          <p className="text-sm text-textMuted" role="status">
            {scanStatus({
              awaiting,
              running,
              pending: data?.counts.pending ?? 0,
              unanswered: data?.unanswered ?? 0,
              // `pending`, not `computedAt`: during a rescan a property that
              // has not been reached yet still carries the answer from the
              // last scan that DID reach it, so counting timestamps counted
              // every property as answered and put the contradiction straight
              // back - "Scanned 20 properties … Scanning — 19 still to go".
              // A property whose failure is still being retried is not
              // finished either, for the same reason.
              answered: properties.filter(
                property => property.computedAt && !property.pending && !property.retrying
              ).length,
              total,
              computedAt: data?.computedAt ?? null,
              actionable: actionable.length,
              blocked: blocked.length,
              unchecked: incomplete.length + failed.length,
              unknown,
            })}
          </p>
        </CardContent>
      </Card>

      {(incomplete.length > 0 || failed.length > 0 || stale.length > 0) && (
        <Card className="border-warning/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TriangleAlert className="h-4 w-4 text-warning" />
              Some properties could not be fully checked
            </CardTitle>
            <CardDescription>
              These are not empty results — they are unknown ones. A gap may exist on these
              properties that this scan could not see.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {failed.map(property => (
              <div key={`failed-${property.propertyId}`} className="text-sm">
                <span className="font-medium text-text">
                  {property.propertyName || property.propertyId}
                </span>
                <p className="text-xs text-textMuted">{property.error}</p>
              </div>
            ))}
            {incomplete.map(property => (
              <div key={`incomplete-${property.propertyId}`} className="text-sm">
                <span className="font-medium text-text">
                  {property.propertyName || property.propertyId}
                </span>
                <ul className="list-disc pl-5 text-xs text-textMuted">
                  {truncationReasons(property.truncation as OrphanGapTruncation).map(reason => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </div>
            ))}
            {stale.map(property => (
              <div key={`stale-${property.propertyId}`} className="text-sm">
                <span className="font-medium text-text">
                  {property.propertyName || property.propertyId}
                </span>
                {/* Said plainly, because the same words without it would be a
                    claim about a scan that has not looked at this property. */}
                <p className="text-xs text-textMuted">
                  Not reached by this scan yet. From the last one that was:
                </p>
                <ul className="list-disc pl-5 text-xs text-textMuted">
                  {property.error && <li>{property.error}</li>}
                  {property.truncation &&
                    truncationReasons(property.truncation).map(reason => (
                      <li key={reason}>{reason}</li>
                    ))}
                </ul>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Offerable now</CardTitle>
        </CardHeader>
        <CardContent>
          {actionable.length === 0 ? (
            <EmptyState
              message={
                running
                  ? 'Scanning — properties appear here as each one finishes.'
                  : !data?.computedAt
                    ? 'Nothing has been scanned yet. The daily scan fills this in, or press Rescan to run one now.'
                    : unknown > 0
                      ? 'Nothing offerable among the properties that could be checked. Some could not be, so a gap may exist that this scan did not see.'
                      : 'No offerable orphan nights. An orphan night only exists when two bookings sit either side of a hole shorter than the minimum stay, so a quiet season usually has none.'
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property &amp; dates</TableHead>
                  <TableHead>Gap</TableHead>
                  <TableHead>Who to ask</TableHead>
                  <TableHead>Suggested price</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {actionable.map(({ row, stale }) => (
                  <GapRows key={row.gapKey} row={row} stale={stale} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {blocked.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Found, but not offerable</CardTitle>
            <CardDescription>
              Shown with the reason rather than hidden, so a gap you can see on the calendar never
              silently disappears from this page.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property &amp; dates</TableHead>
                  <TableHead>Gap</TableHead>
                  <TableHead>Why not</TableHead>
                  <TableHead>Suggested price</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blocked.map(({ row, stale }) => (
                  <GapRows key={row.gapKey} row={row} stale={stale} />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
